// タッチ端末向けの 2 本指ピンチズーム。
//
// 振る舞い:
// - キャンバス面へタッチが 2 点そろった時点でピンチを開始する。倍率は 2 指の間隔の比で変わり、
//   開始時の中点が指していたキャンバス上の点を、操作中ずっと現在の中点へ合わせる
//   （＝拡大縮小と 2 指の平行移動によるパンが、同じジェスチャで同時に成立する）。
// - ピンチ開始時は、1 本目の指で進行中だったジェスチャ（選択矩形・ペン描画・図形配置・
//   オブジェクト移動 / リサイズ / 回転）を確定させずに取り消す。取り消しの合図には、既存フックが
//   すでに終了イベントとして受け取っている pointercancel を合成して配送する。
// - ピンチが成立してからキャンバス面のタッチが 0 本になるまでは「抑止中」とし、その間の
//   pointerdown / pointermove をキャプチャフェーズで止めて既存フックへ渡さない。指を離す順序で
//   意図しない選択や描画が始まらないようにするためである。
//
// 設計上の判断:
// - 既存フック（use-pointer-mode / use-marquee-selection / use-object-drag / use-resize-handle /
//   use-rotation-handle）へピンチ中フラグを配らない。フックの数だけ購読と破棄の分岐が増え、
//   破棄漏れの経路が生まれるため、終了イベントの再利用で完結させる。
// - キャプチャフェーズで登録するのは、既存フック（バブルフェーズ）より先に 2 点目の接触を検知して
//   取り消しを走らせるためと、キャンバス面の子孫（オブジェクト要素・各ハンドル）に登録された
//   リスナーまでまとめて抑止するためである（キャプチャは親から子へ伝わるので親で止めれば届かない）。
// - マウス・ペン（pointerType !== "touch"）は一切扱わない。トラックパッドのピンチは ctrlKey 付きの
//   wheel として届くため use-wheel-pan-zoom が従来どおり処理する。

import { useEffect, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { applyTouchPinch, type TouchPinchAnchor } from "../../../lib/canvas-coordinates";

/** キャンバス面に乗っているタッチポインタ 1 点ぶんの追跡情報。 */
interface TrackedPointer {
  x: number;
  y: number;
  /** pointerdown を受けた要素。合成 pointercancel の配送先に使う。 */
  readonly target: EventTarget | null;
}

/** 2 指の間隔（px）と中点（screen 座標）。 */
function pinchMetricsOf(
  a: TrackedPointer,
  b: TrackedPointer
): { distance: number; center: { x: number; y: number } } {
  return {
    distance: Math.hypot(b.x - a.x, b.y - a.y),
    center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
  };
}

export function useTouchPinchZoom(containerRef: RefObject<HTMLElement | null>): void {
  const setView = useCanvasStore((s) => s.setView);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // キャンバス面に乗っているタッチポインタ。ピンチの計算には挿入順で先頭 2 点だけを使い、
    // 3 点目以降は記録するだけにする（指が増減するたびに基準の 2 点が入れ替わると画面が飛ぶ）。
    const pointers = new Map<number, TrackedPointer>();
    // ピンチ中の基準値。非ピンチ時は undefined。
    let anchor: TouchPinchAnchor | undefined;
    // ピンチ成立後、タッチが 0 本になるまで既存フックへの配送を止める。
    let suppressing = false;
    // 自分が配送している合成 pointercancel を、自分の終了処理で拾い直さないための目印。
    let dispatchingCancel = false;

    /** 挿入順で先頭 2 点を返す。2 点未満なら null。 */
    const firstTwoPointers = (): [TrackedPointer, TrackedPointer] | null => {
      const iterator = pointers.values();
      const first = iterator.next();
      if (first.done) return null;
      const second = iterator.next();
      if (second.done) return null;
      return [first.value, second.value];
    };

    /**
     * 記録済みのタッチポインタへ合成 pointercancel を配送し、進行中ジェスチャを取り消す。
     * 配送先は各ポインタの pointerdown の target とする。キャンバス面へ配送すると、子要素で
     * 進行中のオブジェクト移動・リサイズ・回転へ届かない（イベントは子から親へバブルするため）。
     * target が既に DOM から外れている場合はキャンバス面へフォールバックする。
     * 挿入順に配送するのは、先に始まったジェスチャから順に破棄するためである。
     */
    const cancelPointerGestures = (): void => {
      dispatchingCancel = true;
      try {
        for (const [pointerId, pointer] of pointers) {
          const node = pointer.target;
          const target = node instanceof Node && node.isConnected ? node : container;
          target.dispatchEvent(
            new PointerEvent("pointercancel", {
              bubbles: true,
              composed: true,
              pointerId,
              pointerType: "touch",
              clientX: pointer.x,
              clientY: pointer.y,
            })
          );
        }
      } finally {
        dispatchingCancel = false;
      }
    };

    /**
     * タッチが 2 点そろい、かつ間隔が 0 より大きければピンチを開始する。
     * 間隔 0（ほぼ同一点への同時接触）では開始せず、以降の pointermove で改めて判定する。
     */
    const tryStartPinch = (): void => {
      if (anchor) return;
      const pair = firstTwoPointers();
      if (!pair) return;
      const metrics = pinchMetricsOf(pair[0], pair[1]);
      if (!(metrics.distance > 0)) return;
      anchor = {
        view: useCanvasStore.getState().view,
        distance: metrics.distance,
        center: metrics.center,
      };
      cancelPointerGestures();
      suppressing = true;
      // キャンバス面上の pointerdown は canvas-stage が自動で arm するが、取り消しの影響を
      // 受けないよう明示的に要求する（use-ctrl-wheel-zoom と同じ順序）。
      useCanvasStore.getState().requestViewSyncArm();
    };

    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== "touch") return;
      // 主ポインタ（isPrimary）のタッチは、他のタッチが 1 本も乗っていないときにだけ発生する。
      // すなわちこの時点で残っている記録は、pointerup / pointercancel を取りこぼした残骸である。
      // 取りこぼすと抑止が永久に解けずタッチ操作が効かなくなるため、ここで捨てて自己修復する。
      if (event.isPrimary) {
        pointers.clear();
        anchor = undefined;
        suppressing = false;
      }
      pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
        target: event.target,
      });
      tryStartPinch();
      // 抑止の判定はピンチ開始処理の後に行う。先に判定すると、ピンチを開始したその 2 点目だけが
      // 既存フックへ届き、2 点目の指の下にあるオブジェクトのドラッグや新しい選択矩形が始まる。
      // 3 点目以降の pointerdown も同じ判定で止まる。
      if (suppressing) event.stopPropagation();
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (event.pointerType !== "touch") return;
      const tracked = pointers.get(event.pointerId);
      if (tracked) {
        tracked.x = event.clientX;
        tracked.y = event.clientY;
      }
      // 間隔 0 で開始を見送っていた場合に備え、move でも開始判定を行う。
      tryStartPinch();
      if (!suppressing) return;
      event.stopPropagation();
      if (!anchor) return;
      const pair = firstTwoPointers();
      if (!pair) return;
      event.preventDefault();
      setView(applyTouchPinch(anchor, pinchMetricsOf(pair[0], pair[1])));
    };

    // 指を離す / 中断されたときの後始末。抑止中でもこれらのイベント自体は止めず、既存フックが
    // 自分の保留状態を解除できるようにする。
    //
    // window で受けるのは、合成 pointercancel を受けた既存フックが pointer capture を解放した
    // 場合に、以降のイベントがキャンバス面の外（ツールバー等）を経路にし得るためである。
    // キャンバス面だけで受けると pointerup を取りこぼし、抑止が解けないまま残る。
    const onPointerEnd = (event: PointerEvent): void => {
      // 自分が配送した合成 pointercancel では、開始したばかりのピンチを終了させない。
      if (dispatchingCancel) return;
      if (event.pointerType !== "touch") return;
      if (!pointers.delete(event.pointerId)) return;
      // 2 点未満になったらピンチ終了。残った 1 点でピンチを再開したりドラッグを始めたりしない。
      if (pointers.size < 2) anchor = undefined;
      if (pointers.size === 0) suppressing = false;
    };

    const listenerOptions = { capture: true, passive: false } as const;
    container.addEventListener("pointerdown", onPointerDown, listenerOptions);
    container.addEventListener("pointermove", onPointerMove, listenerOptions);
    window.addEventListener("pointerup", onPointerEnd, { capture: true });
    window.addEventListener("pointercancel", onPointerEnd, { capture: true });
    return () => {
      container.removeEventListener("pointerdown", onPointerDown, listenerOptions);
      container.removeEventListener("pointermove", onPointerMove, listenerOptions);
      window.removeEventListener("pointerup", onPointerEnd, { capture: true });
      window.removeEventListener("pointercancel", onPointerEnd, { capture: true });
      pointers.clear();
      anchor = undefined;
      suppressing = false;
    };
  }, [containerRef, setView]);
}
